import { resendSignUpCode, signUp } from "aws-amplify/auth";
import React, { FormEvent, useEffect, useState } from "react";
import "../../amplifyClient";
import { Button } from "@shared/ui";
import CarrierMark from "../../components/CarrierMark";
import { InlineError } from "../../components/Field";
import StepBody from "../../components/StepBody";
import StepFooter from "../../components/StepFooter";
import StepHeading from "../../components/StepHeading";
import PhoneInputField, {
  isPhoneFieldError,
  PHONE_FIELD_ERRORS,
  validatePhoneNumber,
} from "../../components/PhoneInputField";
import { DEFAULT_ERROR_MESSAGE, SKIP_PASSPORT } from "../../constants";
import { handleReturningUserSignIn } from "../../lib/authUtility";
import { getNextStepBeforeSuccess } from "../../lib/utility";

interface AxleAccountSignUpProps {
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
  step: string;
  nextStep: (nextStep: string, noHistory?: boolean) => void;
  accountInfo: any;
  loginInformation: any;
  setIsReturningUserInSignUpFlow: (isReturningUser: boolean) => void;
  posthog: any;
  session: any;
}

export default function AxleAccountSignUp({
  phoneNumber,
  setPhoneNumber,
  nextStep,
  accountInfo,
  loginInformation,
  setIsReturningUserInSignUpFlow,
  posthog,
  step,
  session,
}: AxleAccountSignUpProps) {
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Effect for PostHog tracking
  useEffect(() => {
    posthog.capture("$pageview", { step });
  }, [posthog]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);
    setError("");

    if (!phoneNumber) {
      setError(PHONE_FIELD_ERRORS.missing);
      setIsSubmitting(false);
      return;
    }

    if (!validatePhoneNumber(phoneNumber, "US")) {
      setError(PHONE_FIELD_ERRORS.invalid);
      setIsSubmitting(false);
      return;
    }

    if (!accountInfo.id) {
      console.log("No linked account found...");
      setIsSubmitting(false);
      nextStep("axle-error");
      return;
    }

    try {
      const { nextStep: step } = await signUp({
        username: phoneNumber,
        options: {
          userAttributes: {
            phone_number: phoneNumber,
            "custom:accounts": JSON.stringify({
              accounts: [], // Keep empty for now... this will be updated in the EnterOTP step
            }),
          },
          autoSignIn: {
            authFlowType: "USER_AUTH",
          },
        },
      });

      // Log the full response to see available properties
      console.log("Sign-up response:", JSON.stringify(step, null, 2));

      if (step?.signUpStep === "CONFIRM_SIGN_UP") {
        // Check if we have code delivery details
        if (step.codeDeliveryDetails) {
          console.log(
            "Verification code sent via",
            step.codeDeliveryDetails.deliveryMedium,
            "to",
            step.codeDeliveryDetails.destination
          );

          if (step.codeDeliveryDetails.deliveryMedium === "SMS") {
            nextStep("enter-otp"); // ask user for the six-digit code
          } else {
            setError(
              "Verification code was not sent via SMS. Please try again."
            );
          }
        } else {
          console.warn("No code delivery details found in response");
          nextStep("enter-otp"); // fallback to original behavior
        }
      } else {
        console.error("Unexpected sign-up step:", step?.signUpStep);
        setError("Sign-up failed. Please try again.");
      }
    } catch (err: any) {
      if (err.name === "UsernameExistsException") {
        // Cannot tell if user is confirmed or unconfirmed from this error alone
        // Try to resend sign-up code - this will work for unconfirmed users but fail for confirmed users
        try {
          await resendSignUpCode({ username: phoneNumber });
          // If resendSignUpCode succeeds, the user exists but is unconfirmed
          console.log(
            "User exists but is unconfirmed (needs OTP verification)"
          );
          nextStep("enter-otp");
        } catch (resendError: any) {
          console.log("Error from resendSignUpCode:", resendError);
          // If we get an error about user being confirmed, they are a returning user
          if (
            resendError.message &&
            resendError.message.includes("User is already confirmed")
          ) {
            console.log("User is a returning user who is already confirmed");
            // Track returning user detected in signup flow
            posthog.capture("returning_user_detected_in_signup");
            setIsReturningUserInSignUpFlow(true);
            await handleReturningUserSignIn(
              phoneNumber,
              nextStep,
              setError,
              setIsSubmitting
            );
          } else {
            // Some other error occurred
            setError(
              "Something went wrong. Please check your phone number and try again."
            );
          }
        }
      } else if (err.message && err.message.includes("SMS")) {
        setError(
          "Unable to send verification SMS. Please verify your phone number is correct."
        );
      } else if (err.name === "LimitExceededException") {
        setError(
          "You've attempted too many times. Please try again after some time."
        );
      } else if (err.name === "NetworkError") {
        console.log("Network error... asking user to try again");
        setError(DEFAULT_ERROR_MESSAGE);
      } else {
        // Unknown error – log at error level so it surfaces in Sentry
        console.error("Sign-up failed: ", err.name);
        console.log(JSON.stringify(err, null, 2));
        setError(
          "Something went wrong. Please check your phone number and try again."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex min-h-0 flex-1 flex-col phone-entry-screen"
    >
      <StepBody>
        <div className="flex flex-col gap-8">
          <CarrierMark className="enter-phone-illustration max-h-14" loginInformation={loginInformation} />

          {/* Main content */}
          <div className="main-copy">
            <StepHeading
              title={`Save ${loginInformation.carrier.name} for next time`}
              description="Connect faster next time across thousands of Axle-powered apps."
            />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {/* Error message */}
          {error && !isPhoneFieldError(error) && (
            <div className="rounded border border-danger-border p-3">
              <InlineError>{error}</InlineError>
            </div>
          )}

          {/* Phone input */}
          <PhoneInputField
            value={phoneNumber}
            onChange={setPhoneNumber}
            country="US"
            onCountryChange={() => {}}
            showCountryFlags={true}
            disabled={isSubmitting}
            error={isPhoneFieldError(error) ? error : undefined}
          />
        </div>
      </StepBody>

      {/* Action buttons */}
      <StepFooter>
        <p className="pb-2 text-center text-small  text-ink-secondary">
          By selecting “Continue”, you authorize Axle to send you a one-time SMS
          and agree to the{" "}
          <a
            target="_blank"
            href="https://www.axle.insure/legal/end-user-terms"
            className="font-medium text-ink underline underline-offset-2"
          >
            End User Terms
          </a>{" "}
          and{" "}
          <a
            target="_blank"
            href="https://www.axle.insure/legal/privacy-policy"
            className="font-medium text-ink underline underline-offset-2"
          >
            Privacy Policy
          </a>
          . Message and data rates may apply.
        </p>
        <Button
          name="continue"
          type="submit"
          onClick={() => {
            posthog.capture("axle_passport_sign_up_continue");
          }}
        >
          Continue
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            posthog.capture("axle_passport_sign_up_skip");
            nextStep(getNextStepBeforeSuccess(session));
          }}
          name="skip"
          type="button"
        >
          {SKIP_PASSPORT}
        </Button>
      </StepFooter>
    </form>
  );
}
